import { useAuth } from '@/hooks/useAuth'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AccountSettings,
  SocialMediaSettings,
  AdditionalInfoSettings,
  DocumentsSettings,
  BrandsSettings,
  DangerZoneSettings,
} from '@/components/settings'

const Settings = () => {
  const {
    athlete,
    isLoading,
    profileLoading,
    sportsLoading,
    disconnectSocialMedia,
    updatePreferences,
    updateSports,
    updateSchool,
    updateProfile,
    updateAvatar,
    refreshUser,
  } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get active tab from URL, default to 'account'
  const validTabs = ['account', 'documents', 'social', 'managers', 'additional-info']
  const tabFromUrl = searchParams.get('tab')
  const activeTab = validTabs.includes(tabFromUrl || '') ? tabFromUrl : 'account'
  const isWelcome = searchParams.get('welcome') === 'true'

  // Handle tab change by updating URL
  const handleTabChange = (newTab: string) => {
    if (validTabs.includes(newTab)) {
      setSearchParams({ tab: newTab })
    }
  }

  // Removed visibility-based refresh to avoid tab-switch refresh behavior.

  if (isLoading && !athlete) {
    return (
      <div className="w-full max-w-full pb-4 min-h-screen bg-transparent">
        <div className="mb-4 md:mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Settings</h1>
            <p className="text-gray-400">Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-full pb-4">
      <div className="mb-4 md:mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400">Manage your account and preferences</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full mb-4 bg-white/10 border border-white/10 overflow-x-auto justify-start">
          <TabsTrigger
            value="account"
            className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
          >
            Account
          </TabsTrigger>
          <TabsTrigger
            value="documents"
            className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
          >
            Documents
          </TabsTrigger>
          <TabsTrigger
            value="social"
            className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
          >
            Social Media
          </TabsTrigger>
          <TabsTrigger
            value="managers"
            className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
          >
            Managers
          </TabsTrigger>
          <TabsTrigger
            value="additional-info"
            className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
          >
            Additional Info
          </TabsTrigger>
        </TabsList>

        <TabsContent value="account" className="space-y-6">
          <AccountSettings
            athlete={athlete}
            profileLoading={profileLoading}
            updateProfile={updateProfile}
            updateAvatar={updateAvatar}
          />
          <DangerZoneSettings />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsSettings athlete={athlete} />
        </TabsContent>

        <TabsContent value="social">
          <SocialMediaSettings
            athlete={athlete}
            isWelcome={isWelcome}
            disconnectSocialMedia={disconnectSocialMedia}
          />
        </TabsContent>

        <TabsContent value="managers">
          <BrandsSettings athlete={athlete} />
        </TabsContent>

        <TabsContent value="additional-info">
          <AdditionalInfoSettings
            athlete={athlete}
            updatePreferences={updatePreferences}
            updateSports={updateSports}
            updateSchool={updateSchool}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default Settings
